/**
 * O CANAL DAS TELAS DESTE APARELHO — "estavel" ou "teste" (20/10/2026; conserto do C4).
 *
 * O DEFEITO: a escolha morava só no localStorage da WebView (`telas:canal`), e o Mi 9T consultou o canal "estavel" o dia
 * inteiro, inclusive depois de a opção ser ligada (log do Nginx, 09h14–10h56). O localStorage do Chromium vai ao disco com
 * atraso, em lote; o roteiro manda ligar a opção e logo em seguida FECHAR o app pelos recentes — o processo morre antes da
 * gravação chegar ao disco, e a próxima abertura lê "nada" = estável. E a consulta só acontecia uma vez por abertura, nunca
 * na hora em que a opção mudava.
 *
 * O CONSERTO, em duas peças:
 *   1. a escolha mora no armazenamento NATIVO (`@capacitor/preferences`: SharedPreferences no Android, UserDefaults no
 *      iOS), que o sistema grava com a Activity — e não depende da WebView. O localStorage fica como espelho, para a
 *      primeira tela ler sem esperar, e a escolha antiga (só no localStorage) é levada para o nativo na primeira leitura;
 *   2. mudar a opção avisa o atualizador (`EVENTO_DO_CANAL`), que consulta o servidor NA HORA — não espera a próxima
 *      abertura. Ver `AtualizadorDeTelas.tsx`.
 *
 * Fica por APARELHO, não por conta: sair ou trocar de conta não apaga (é assim que o professor testa as telas como aluno).
 * O armazenamento é injetável só para o teste (`backend/testes-cruzados/canalDoAparelho.test.ts`).
 */
export type Canal = 'teste' | 'estavel';
export const CHAVE_DO_CANAL = 'telas:canal';
export const EVENTO_DO_CANAL = 'telas:canal-mudou';

export interface ArmazemNativo {
  get(o: { key: string }): Promise<{ value: string | null }>;
  set(o: { key: string; value: string }): Promise<void>;
  remove(o: { key: string }): Promise<void>;
}
type Pagina = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * O ARMAZÉM NATIVO, como OBJETO COMUM — nunca o proxy do plugin (20/10/2026, segundo conserto). O objeto do plugin no
 * Capacitor é um Proxy em que QUALQUER propriedade vira chamada nativa, inclusive `then` (`@capacitor/core`, o `get` do
 * `registerPlugin` não trata `then`). Devolvê-lo de uma função `async` faz a promessa chamar `Preferences.then(...)` no
 * nativo, e ela nunca termina: no Mi 9T a caixa não gravava (a gravação esperava para sempre) e o atualizador nunca chegava
 * a consultar o servidor (nenhuma linha `[aplicativo]` de telas desde 13h35). Aqui o proxy fica guardado DENTRO das funções.
 * `carregar` é injetável só para o teste, que usa um proxy igual ao do Capacitor.
 */
export async function armazemDoPlugin(carregar: () => Promise<{ Preferences: ArmazemNativo }> = () => import('@capacitor/preferences') as unknown as Promise<{ Preferences: ArmazemNativo }>): Promise<ArmazemNativo> {
  const { Preferences } = await carregar();
  return {
    get: (o) => Preferences.get(o),
    set: (o) => Preferences.set(o),
    remove: (o) => Preferences.remove(o),
  };
}
async function armazemPadrao(): Promise<ArmazemNativo | null> {
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (!Capacitor.isNativePlatform()) return null;
    return await armazemDoPlugin();
  } catch { return null; }
}
const paginaPadrao = (): Pagina | null => { try { return localStorage; } catch { return null; } };

/**
 * O CANAL DE QUEM NUNCA ESCOLHEU (31/10/2026, bancada, rodada 3). O APK de teste (…app.teste) começa no TESTE: no estável
 * ele baixava as telas de 30/10, que não sabem que esse pacote não tem o Firebase — o registro do push depois do login
 * fechava o app no Mi 9T. Os outros apps começam no estável, como sempre. Injetável só para o teste.
 */
async function canalDeQuemNuncaEscolheu(): Promise<Canal> {
  try {
    const { App } = await import('@capacitor/app');
    return (await App.getInfo()).id?.endsWith('.teste') ? 'teste' : 'estavel';
  } catch { return 'estavel'; }
}

/** O canal, lido do nativo (com a migração da escolha antiga, que estava só no localStorage). */
export async function lerCanal(armazem?: ArmazemNativo | null, pagina: Pagina | null = paginaPadrao(), semEscolha: () => Promise<Canal> = canalDeQuemNuncaEscolheu): Promise<Canal> {
  // SÓ no pacote do passo 4b do roteiro do Mi 9T (build com VITE_TESTE_ATUALIZADOR_TRAVADO=1): o canal nunca é lido — imita o
  // 28d0225 (a lista abre, o atualizador trava). A prova de vida não termina e o plugin tem de desfazer o pacote. No build
  // normal a variável não existe e o trecho sai.
  // Escrito assim de propósito: o Vite troca `import.meta.env.VITE_…` pelo valor na compilação (a forma com `?.` ele não
  // troca); o `typeof` protege o teste em Node, onde `import.meta.env` não existe.
  if (typeof import.meta.env !== 'undefined' && import.meta.env.VITE_TESTE_ATUALIZADOR_TRAVADO === '1') return new Promise<Canal>(() => { /* travado de propósito */ });
  const nativo = armazem === undefined ? await armazemPadrao() : armazem;
  const doEspelho = (() => { try { return pagina?.getItem(CHAVE_DO_CANAL) === 'teste' ? 'teste' : null; } catch { return null; } })();
  if (!nativo) return doEspelho ?? 'estavel';
  try {
    const { value } = await nativo.get({ key: CHAVE_DO_CANAL });
    if (value === 'teste' || value === 'estavel') return value;
    if (doEspelho) { await nativo.set({ key: CHAVE_DO_CANAL, value: 'teste' }); return 'teste'; } // a escolha de antes do conserto
    // Gravado: as telas de antes deste conserto (que não conhecem o padrão do APK de teste) leem "teste" também.
    const padrao = await semEscolha();
    if (padrao === 'teste') await nativo.set({ key: CHAVE_DO_CANAL, value: 'teste' });
    return padrao;
  } catch { return doEspelho ?? 'estavel'; }
}

/** Grava a escolha no nativo (e no espelho) e avisa o atualizador para consultar o servidor já. */
export async function definirCanal(canal: Canal, armazem?: ArmazemNativo | null, pagina: Pagina | null = paginaPadrao(), avisar: (() => void) | null = avisoPadrao): Promise<void> {
  const nativo = armazem === undefined ? await armazemPadrao() : armazem;
  try { if (canal === 'teste') pagina?.setItem(CHAVE_DO_CANAL, 'teste'); else pagina?.removeItem(CHAVE_DO_CANAL); } catch { /* sem espelho */ }
  // "estavel" é gravado, e não apagado: assim a migração da escolha antiga não a traz de volta.
  if (nativo) await nativo.set({ key: CHAVE_DO_CANAL, value: canal });
  avisar?.();
}
function avisoPadrao() { try { window.dispatchEvent(new Event(EVENTO_DO_CANAL)); } catch { /* fora do navegador */ } }

/** A primeira tela, sem esperar o nativo: o espelho. A verdade é `lerCanal`. */
export function canalDoEspelho(pagina: Pagina | null = paginaPadrao()): Canal {
  try { return pagina?.getItem(CHAVE_DO_CANAL) === 'teste' ? 'teste' : 'estavel'; } catch { return 'estavel'; }
}

/**
 * AS VERSÕES QUE FALHARAM NESTE APARELHO (20/10/2026). O plugin volta sozinho ao pacote anterior quando o novo não avisa
 * "pronto" — e APAGA o que falhou (`autoDeleteFailed`), de modo que `list()` não o mostra mais. Sem memória, o atualizador
 * baixaria a mesma versão quebrada de novo a cada abertura. A falha é lida uma vez (`getFailedUpdate`, no
 * `AtualizadorDeTelas`) e guardada aqui, no armazenamento nativo; só uma versão NOVA tira o aparelho daí.
 */
export const CHAVE_DAS_FALHAS = 'telas:versoes-que-falharam';
export async function versoesQueFalharam(armazem?: ArmazemNativo | null): Promise<string[]> {
  const nativo = armazem === undefined ? await armazemPadrao() : armazem;
  if (!nativo) return [];
  try { const { value } = await nativo.get({ key: CHAVE_DAS_FALHAS }); const l = value ? JSON.parse(value) : []; return Array.isArray(l) ? l.filter((v) => typeof v === 'string') : []; } catch { return []; }
}
export async function registrarFalha(versao: string, armazem?: ArmazemNativo | null): Promise<void> {
  const nativo = armazem === undefined ? await armazemPadrao() : armazem;
  if (!nativo || !versao || versao === 'builtin') return;
  const atuais = await versoesQueFalharam(nativo);
  if (atuais.includes(versao)) return;
  await nativo.set({ key: CHAVE_DAS_FALHAS, value: JSON.stringify([...atuais, versao].slice(-20)) });
}

/** O PACOTE BAIXADO À ESPERA DA PRÓXIMA ABERTURA (`trocaDasTelas.ts`), no armazenamento nativo. */
export const CHAVE_DO_PENDENTE = 'telas:pendente';
export async function lerPendente(armazem?: ArmazemNativo | null): Promise<{ versao: string; abertura: string } | null> {
  const nativo = armazem === undefined ? await armazemPadrao() : armazem;
  if (!nativo) return null;
  try { const { value } = await nativo.get({ key: CHAVE_DO_PENDENTE }); const p = value ? JSON.parse(value) : null; return p && typeof p.versao === 'string' && typeof p.abertura === 'string' ? p : null; } catch { return null; }
}
export async function anotarPendente(p: { versao: string; abertura: string } | null, armazem?: ArmazemNativo | null): Promise<void> {
  const nativo = armazem === undefined ? await armazemPadrao() : armazem;
  if (!nativo) return;
  if (p) await nativo.set({ key: CHAVE_DO_PENDENTE, value: JSON.stringify(p) }); else await nativo.remove({ key: CHAVE_DO_PENDENTE });
}

/** O DOWNLOAD COMEÇADO (`trocaDasTelas.ts`): versão e abertura — para não começar dois na mesma abertura. */
export const CHAVE_DO_DOWNLOAD = 'telas:download';
export async function lerDownload(armazem?: ArmazemNativo | null): Promise<{ versao: string; abertura: string } | null> {
  const nativo = armazem === undefined ? await armazemPadrao() : armazem;
  if (!nativo) return null;
  try { const { value } = await nativo.get({ key: CHAVE_DO_DOWNLOAD }); const d = value ? JSON.parse(value) : null; return d && typeof d.versao === 'string' && typeof d.abertura === 'string' ? d : null; } catch { return null; }
}
export async function anotarDownload(d: { versao: string; abertura: string } | null, armazem?: ArmazemNativo | null): Promise<void> {
  const nativo = armazem === undefined ? await armazemPadrao() : armazem;
  if (!nativo) return;
  if (d) await nativo.set({ key: CHAVE_DO_DOWNLOAD, value: JSON.stringify(d) }); else await nativo.remove({ key: CHAVE_DO_DOWNLOAD });
}

/**
 * A DECISÃO DA ÚLTIMA ABERTURA (`trocaNaAbertura.ts`), para a prova de vida mandar ao servidor (`abertura=` na consulta; a
 * linha `[aplicativo]` a grava). No caso "aplicando" o app recarrega antes de consultar: quem a manda é a primeira consulta
 * das telas novas — por isso a abertura seguinte não sobrescreve uma decisão que ainda não foi mandada, salvo com outra
 * que não seja "seguiu".
 */
export const CHAVE_DA_DECISAO = 'telas:decisao-da-abertura';
export async function guardarDecisao(resultado: string, armazem?: ArmazemNativo | null): Promise<void> {
  const nativo = armazem === undefined ? await armazemPadrao() : armazem;
  if (!nativo) return;
  if (resultado === 'seguiu') { const { value } = await nativo.get({ key: CHAVE_DA_DECISAO }); if (value) return; }
  await nativo.set({ key: CHAVE_DA_DECISAO, value: resultado });
}
export async function lerDecisao(armazem?: ArmazemNativo | null): Promise<string | null> {
  const nativo = armazem === undefined ? await armazemPadrao() : armazem;
  if (!nativo) return null;
  try { return (await nativo.get({ key: CHAVE_DA_DECISAO })).value; } catch { return null; }
}
/** Só depois de o servidor responder: sem internet, a decisão espera a próxima consulta. */
export async function apagarDecisao(armazem?: ArmazemNativo | null): Promise<void> {
  const nativo = armazem === undefined ? await armazemPadrao() : armazem;
  if (nativo) await nativo.remove({ key: CHAVE_DA_DECISAO }).catch(() => undefined);
}
